import { KeyboardEvent, useEffect, useRef } from "react";

const SIZE = 150;
const CENTER = SIZE / 2;
const OUTER_RADIUS = 56;
const INNER_RADIUS = 22;
const LABEL_RADIUS = 68;

// 16-point compass names in Portuguese (L = leste, O = oeste).
const COMPASS_POINTS = [
  "N",
  "NNE",
  "NE",
  "ENE",
  "L",
  "ESE",
  "SE",
  "SSE",
  "S",
  "SSO",
  "SO",
  "OSO",
  "O",
  "ONO",
  "NO",
  "NNO",
];

const CARDINALS = [
  { label: "N", angle: 0 },
  { label: "L", angle: 90 },
  { label: "S", angle: 180 },
  { label: "O", angle: 270 },
];

export function compassPointName(degrees: number) {
  const normalized = ((degrees % 360) + 360) % 360;
  return COMPASS_POINTS[Math.round(normalized / 22.5) % 16];
}

// Compass angles grow clockwise from north (0° at the top of the SVG).
function polar(radius: number, degrees: number) {
  const radians = (degrees * Math.PI) / 180;
  return {
    x: CENTER + radius * Math.sin(radians),
    y: CENTER - radius * Math.cos(radians),
  };
}

function sectorPath(start: number, end: number) {
  const outerStart = polar(OUTER_RADIUS, start);
  const outerEnd = polar(OUTER_RADIUS, end);
  const innerEnd = polar(INNER_RADIUS, end);
  const innerStart = polar(INNER_RADIUS, start);
  const largeArc = end - start > 180 ? 1 : 0;
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${INNER_RADIUS} ${INNER_RADIUS} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ");
}

interface CompassRoseProps {
  values: number[];
  value: number;
  onChange: (value: number) => void;
  ariaLabel: string;
}

/**
 * Circular sector picker for wind-direction dimensions. Each value is drawn as
 * a wedge centered on its angle, with 0° pointing north and angles growing
 * clockwise (N → L → S → O).
 */
export function CompassRose({
  values,
  value,
  onChange,
  ariaLabel,
}: CompassRoseProps) {
  const sorted = [...values].sort((a, b) => a - b);
  const halfWidth = sorted.length > 0 ? 180 / sorted.length : 0;
  const groupRef = useRef<SVGGElement>(null);

  // Roving focus: when the selection changes while focus is inside the group
  // (arrow-key navigation), move focus to the newly selected sector so the
  // focused radio always matches the checked one.
  useEffect(() => {
    const group = groupRef.current;
    if (!group || !group.contains(document.activeElement)) return;
    group.querySelector<SVGPathElement>('[aria-checked="true"]')?.focus();
  }, [value]);

  // Handled per sector so arrows move from the focused radio (not only the
  // checked one) and Space checks it, as SVG paths get no native activation.
  const handleKeyDown = (
    event: KeyboardEvent<SVGPathElement>,
    option: number
  ) => {
    if (event.key === " ") {
      event.preventDefault();
      onChange(option);
      return;
    }
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
        ? -1
        : 0;
    if (step === 0 || sorted.length === 0) return;
    event.preventDefault();
    const index = sorted.indexOf(option);
    const next = (index + step + sorted.length) % sorted.length;
    onChange(sorted[next]);
  };

  return (
    <svg
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="select-none"
    >
      <circle
        cx={CENTER}
        cy={CENTER}
        r={OUTER_RADIUS}
        fill="none"
        stroke="var(--border)"
      />

      <g ref={groupRef} role="radiogroup" aria-label={ariaLabel}>
        {sorted.map((option) => {
          const selected = option === value;
          return (
            <path
              key={option}
              role="radio"
              aria-checked={selected}
              aria-label={`${option}° (${compassPointName(option)})`}
              tabIndex={selected ? 0 : -1}
              d={sectorPath(option - halfWidth, option + halfWidth)}
              onClick={() => onChange(option)}
              onKeyDown={(event) => handleKeyDown(event, option)}
              className="cursor-pointer outline-none transition-[fill] hover:opacity-80 focus-visible:opacity-80"
              style={{
                fill: selected ? "var(--primary)" : "var(--muted)",
                stroke: "white",
                strokeWidth: 1.5,
              }}
            >
              <title>{`${option}° — ${compassPointName(option)}`}</title>
            </path>
          );
        })}
      </g>

      {CARDINALS.map(({ label, angle }) => {
        const { x, y } = polar(LABEL_RADIUS, angle);
        return (
          <text
            key={label}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            className="pointer-events-none text-[11px] font-semibold"
            style={{ fill: label === "N" ? "var(--primary)" : "currentColor" }}
          >
            {label}
          </text>
        );
      })}

      <text
        x={CENTER}
        y={CENTER - 5}
        textAnchor="middle"
        dominantBaseline="central"
        className="pointer-events-none text-[12px] font-semibold"
        style={{ fill: "var(--primary)" }}
      >
        {`${value}°`}
      </text>
      <text
        x={CENTER}
        y={CENTER + 8}
        textAnchor="middle"
        dominantBaseline="central"
        className="pointer-events-none text-[9px]"
        style={{ fill: "var(--muted-foreground)" }}
      >
        {compassPointName(value)}
      </text>
    </svg>
  );
}
